import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import heroImage from "../../assets/REMA/hero-image.png";
import { FOCUS_AREAS } from "./constants";

const FocusComponent = React.forwardRef(({ focusIndex }, ref) => {
  return (
    <div
      ref={ref}
      className={` flex items-center gap-4 lg:gap-8 md:max-w-[40%] lg:max-w-[30%] 2xl:max-w-[50%] ${
        focusIndex % 2 !== 0 ? "flex-row" : "md:flex-row-reverse"
      }`}
    >
      <div className="md:border-[10px] border-[#E2EFC8] bg-[#8DBF25] flex items-center justify-center lg:w-[100px] lg:h-[100px] rounded-full text-white aspect-square w-[40px] md:w-[56px] md:h-[56px] ">
        <p className="text-base lg:text-[32px] font-inter font-medium">
          {FOCUS_AREAS[focusIndex].index}
        </p>
      </div>

      <p
        className={`font-inter font-medium lg:text-[18px] xl:leading-[36px] xl:text-[26px] md:max-w-20 2xl:max-w-[307px]  ${
          focusIndex % 2 !== 0 ? "text-left" : "md:text-right "
        }`}
        style={{ textWrap: "balance" }}
      >
        {FOCUS_AREAS[focusIndex].label}
      </p>
    </div>
  );
});

const RemaFocus = () => {
  // Refs for animation elements
  const backgroundRef = useRef(null);
  const innerCircleRef = useRef(null);
  const focusRefs = useRef([]);
  const containerRef = useRef(null);

  // Set refs for focus components
  const setFocusRef = (el, index) => {
    if (el) {
      focusRefs.current[index] = el;
    }
  };

  // GSAP Timeline Animation with ScrollTrigger
  useGSAP(
    () => {
      const tl = gsap.timeline({
        defaults: { ease: "power2.out" },
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%",
          end: "bottom 20%",
          markers: false,
          fastScrollEnd: true,
        },
      });

      // Initial states - hide everything
      gsap.set(backgroundRef.current, {
        clipPath: "circle(0% at 50% 50%)",
      });

      gsap.set(innerCircleRef.current, {
        scale: 0,
        opacity: 0,
      });

      gsap.set(focusRefs.current, {
        opacity: 0,
        y: 50,
        scale: 0.8,
      });

      // Timeline sequence
      tl
        // 1. Background clipPath animation from 0 to full width
        .to(backgroundRef.current, {
          clipPath: "circle(50% at 50% 50%)",
          duration: 1.5,
          ease: "power2.out",
        })
        // 2. Inner circle animation (appears after background)
        .to(
          innerCircleRef.current,
          {
            scale: 1,
            opacity: 1,
            duration: 0.8,
            ease: "elastic.out(1, 0.75)",
          },
          "<"
        ) // Start 0.3s before previous animation ends
        // 3. Focus areas staggered animation
        .to(
          focusRefs.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            stagger: {
              amount: 0.8, // Total time for all staggers
              from: "start", // Start from first element
              ease: "power2.out",
            },
            ease: "back.out(1.7)",
          },
          "-=0.3>"
        ); // Start 0.2s before previous animation ends
    },
    { scope: containerRef }
  );

  return (
    <section className="bg-[#E2EFC8] min-h-[70dvh] flex items-center justify-center">
      <div
        ref={containerRef}
        className="container mx-auto px-4  py-16 lg:py-[100px] font-sora relative"
      >
        <div
          ref={backgroundRef}
          className=" aspect-square md:w-2/3  lg:w-1/2 2xl:w-[3/5]  relative clip overflow-hidden mx-auto"
          style={{
            clipPath: "circle(50% at 50% 50%)",
          }}
        >
          <img
            src={heroImage}
            alt="Hero Background"
            className="w-full h-full object-cover border"
          />
          <div className="absolute inset-0 hero-bg-gradient" />

          {/* INNER CIRCLER */}
          <div
            // ref={innerCircleRef}
            className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-1/2 aspect-square  rounded-full bg-[#E2EFC8] flex items-center justify-center text-center text-2xl font-bold p-4"
          >
            <p className="lg:text-[40px] leading-[1.2] uppercase">
              REMA 2025 <br /> Focus <br /> Areas
            </p>
          </div>
        </div>
        {/* TOP FOCUS PAIR - DESKTOP */}
        <div className="absolute top-16 lg:top-[150px] left-1/2 -translate-x-1/2 w-full max-w-6xl 2xl:max-w-7xl px-4 md:px-0 hidden md:block">
          <div className="flex justify-between items-center">
            <FocusComponent ref={(el) => setFocusRef(el, 0)} focusIndex={0} />
            <FocusComponent ref={(el) => setFocusRef(el, 1)} focusIndex={1} />
          </div>
        </div>

        {/* BOTTOM FOCUS PAIR - DESKTOP */}
        <div className="absolute bottom-16 lg:bottom-[150px] left-1/2 -translate-x-1/2 w-full max-w-6xl 2xl:max-w-7xl hidden md:block px-4 md:px-0">
          <div className="flex justify-between items-center">
            <FocusComponent ref={(el) => setFocusRef(el, 2)} focusIndex={2} />
            <FocusComponent ref={(el) => setFocusRef(el, 3)} focusIndex={3} />
          </div>
        </div>
        <div className=" md:hidden space-y-6 mt-6">
          {FOCUS_AREAS.map((_, index, array) => (
            <React.Fragment key={index}>
              <FocusComponent
                ref={(el) => setFocusRef(el, index + 4)}
                focusIndex={index}
              />
              {index < array.length - 1 && (
                <hr className="border-t border-[#8DBF25]" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RemaFocus;
